import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  InscriptionEntreprisePayload,
  RdvApiService,
} from '../../core/api/rdv-api.service';

@Component({
  selector: 'app-inscription-entreprise',
  imports: [ReactiveFormsModule],
  templateUrl: './inscription-entreprise.html',
  styleUrl: './inscription-entreprise.css',
})
export class InscriptionEntreprise {
  private readonly rdvApi = inject(RdvApiService);
  private readonly fb = inject(FormBuilder);

  protected readonly submitting = signal(false);
  protected readonly submittedSuccess = signal(false);
  protected readonly apiError = signal<string | null>(null);

  readonly form = this.fb.group({
    companyType: this.fb.nonNullable.control<'company' | 'association'>('company', {
      validators: [Validators.required],
    }),
    companyName: this.fb.nonNullable.control('', { validators: [Validators.required] }),
    address: this.fb.nonNullable.control('', { validators: [Validators.required] }),
    zip: this.fb.nonNullable.control('', { validators: [Validators.required] }),
    city: this.fb.nonNullable.control('', { validators: [Validators.required] }),
    numberOfEmployees: this.fb.control<number | null>(null, {
      validators: [Validators.required, Validators.min(1)],
    }),
    firstname: this.fb.nonNullable.control('', { validators: [Validators.required] }),
    lastname: this.fb.nonNullable.control('', { validators: [Validators.required] }),
    telephone: this.fb.nonNullable.control('', { validators: [Validators.required] }),
    email: this.fb.nonNullable.control('', { validators: [Validators.required, Validators.email] }),
    comments: this.fb.nonNullable.control('', { validators: [Validators.required] }),
    acceptDonnees: this.fb.nonNullable.control(false, { validators: [Validators.requiredTrue] }),
  });

  constructor() {
    const kindCtrl = this.form.controls.companyType;
    const employeesCtrl = this.form.controls.numberOfEmployees;

    const syncEmployeesValidators = (kind: 'company' | 'association') => {
      if (kind === 'company') {
        employeesCtrl.setValidators([Validators.required, Validators.min(1)]);
      } else {
        employeesCtrl.clearValidators();
        employeesCtrl.setValue(null, { emitEvent: false });
      }
      employeesCtrl.updateValueAndValidity({ emitEvent: false });
    };

    syncEmployeesValidators(kindCtrl.value);
    kindCtrl.valueChanges.pipe(takeUntilDestroyed()).subscribe(syncEmployeesValidators);
  }

  protected onSubmitFormEntreprise(): void {
    this.apiError.set(null);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const nb = raw.numberOfEmployees;
    if (raw.companyType === 'company' && (nb === null || nb === undefined)) {
      return;
    }

    const payload: InscriptionEntreprisePayload = {
      companyType: raw.companyType,
      companyName: raw.companyName.trim(),
      address: raw.address.trim(),
      zip: raw.zip.trim(),
      city: raw.city.trim(),
      numberOfEmployees: raw.companyType === 'company' ? (nb as number) : 0,
      firstname: raw.firstname.trim(),
      lastname: raw.lastname.trim(),
      telephone: raw.telephone.trim(),
      email: raw.email.trim(),
      comments: raw.comments.trim(),
      acceptDonnees: raw.acceptDonnees,
    };

    this.submitting.set(true);

    this.rdvApi.createEntreprise(payload).subscribe({
      next: (res) => {
        this.submitting.set(false);
        if (res.success) {
          this.submittedSuccess.set(true);
          return;
        }
        this.apiError.set(res.error?.trim() || 'Une erreur est survenue.');
      },
      error: (err: unknown) => {
        this.submitting.set(false);
        if (err instanceof HttpErrorResponse) {
          const body = err.error;
          if (body && typeof body === 'object' && body !== null) {
            if ('success' in body && body.success === false && 'error' in body) {
              const msg = (body as { error?: unknown }).error;
              if (typeof msg === 'string' && msg.trim()) {
                this.apiError.set(msg.trim());
                return;
              }
            }
            if ('error' in body) {
              const msg = (body as { error?: unknown }).error;
              if (typeof msg === 'string' && msg.trim()) {
                this.apiError.set(msg.trim());
                return;
              }
            }
          }
        }
        if (err instanceof Error && err.message) {
          this.apiError.set(err.message);
          return;
        }
        this.apiError.set("Impossible d'envoyer le formulaire. Réessaie plus tard.");
      },
    });
  }
}
